import {
  useEffect,
  useState
} from "react";
import {
  Link
} from "react-router-dom";
import {
  ArrowLeft,
  Wallet
} from "lucide-react";
import {
  getMyFeeLedger
} from "../../../api/fees/feeApi";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Table
} from "../../../components/ui";
import {
  useToast
} from "../../../context/ToastContext";

const formatMoney = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StudentMyLedger = () => {
  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState(null);

  const toast = useToast();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getMyFeeLedger();
        const data = res?.data || res || {};
        setLedger(
          Array.isArray(data.ledger)
            ? data.ledger
            : Array.isArray(data)
            ? data
            : data.dues || data.pending || []
        );
        setSummary(data.summary || null);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load fee ledger.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const ledgerColumns = [
    {
      key: "feeHead",
      header: "Fee Head",
      render: (row) => (
        <span className="font-medium text-xs text-neutral-800 dark:text-neutral-100">
          {row.title || row.feeHeadName || row.feeHeadId?.name || row.name || "—"}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      render: (row) => (
        <span className="text-xs font-medium text-neutral-700 dark:text-neutral-200">
          {formatMoney(row.amount)}
        </span>
      ),
    },
    {
      key: "paid",
      header: "Paid",
      render: (row) => (
        <span className="text-xs font-medium text-emerald-600">
          {formatMoney(row.paid || row.paidAmount)}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Balance",
      render: (row) => (
        <span className="text-xs font-bold text-rose-600">
          {formatMoney(row.balance ?? row.outstanding ?? 0)}
        </span>
      ),
    },
    {
      key: "dueDate",
      header: "Due Date",
      render: (row) => (
        <span className="text-xs text-neutral-600 dark:text-neutral-300">{formatDate(row.dueDate)}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const statusUpper = String(row.status || "").toUpperCase();
        const statusVariant =
          statusUpper === "PAID"
            ? "success"
            : statusUpper === "OVERDUE"
            ? "danger"
            : "warning";
        return <Badge variant={statusVariant}>{row.status || "PENDING"}</Badge>;
      },
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50/50 p-4 sm:p-6 lg:p-8 antialiased">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/student-home">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              Back to Dashboard
            </Button>
          </Link>
        </div>

        <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden">
          <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 p-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-50 text-amber-600">
                <Wallet className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                  My Fee Ledger
                </CardTitle>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  Track fee dues, paid amounts, and outstanding balance
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            

            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-neutral-50/80 border border-neutral-100 dark:border-neutral-800 rounded-xl p-4">
                  <p className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1">
                    Total Due
                  </p>
                  <p className="text-base sm:text-lg font-bold text-neutral-900 dark:text-neutral-100">
                    {formatMoney(summary.totalDue ?? summary.totalAmount)}
                  </p>
                </div>

                <div className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-4">
                  <p className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">
                    Paid
                  </p>
                  <p className="text-base sm:text-lg font-bold text-emerald-700">
                    {formatMoney(summary.totalPaid ?? summary.paid)}
                  </p>
                </div>

                <div className="bg-rose-50/40 border border-rose-100 rounded-xl p-4">
                  <p className="text-[11px] font-semibold text-rose-600 uppercase tracking-wider mb-1">
                    Balance
                  </p>
                  <p className="text-base sm:text-lg font-bold text-rose-700">
                    {formatMoney(summary.balance ?? summary.outstanding)}
                  </p>
                </div>

                <div className="bg-neutral-50/80 border border-neutral-100 dark:border-neutral-800 rounded-xl p-4">
                  <p className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1">
                    Pending Items
                  </p>
                  <p className="text-base sm:text-lg font-bold text-neutral-900 dark:text-neutral-100">
                    {ledger.filter((r) => Number(r.balance ?? r.outstanding ?? 0) > 0).length}
                  </p>
                </div>
              </div>
            )}

            <Table
              columns={ledgerColumns}
              data={ledger}
              isLoading={loading}
              density="comfortable"
              emptyState={<span className="text-xs text-neutral-400 dark:text-neutral-500">No fee records found.</span>}
            />

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to="/student-panel/fees/pay" className="flex-1">
                <Button variant="primary" fullWidth className="py-2.5 text-xs font-semibold">
                  Pay Fees
                </Button>
              </Link>
              <Link to="/student-panel/fees/receipts" className="flex-1">
                <Button variant="outline" fullWidth className="py-2.5 text-xs font-semibold">
                  My Receipts
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentMyLedger;